import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import { Visibility as ViewIcon, Edit as EditIcon, Delete as DeleteIcon, AttachFile, Close as CloseIcon } from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Visit, Medicine } from '../types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Autocomplete from '@mui/material/Autocomplete';

interface Patient {
  id: number;
  name: string;
  opd: string;
  age: number;
  gender: string;
  mobile?: string;
  address?: string;
  date?: string;
  last_visit_date?: string;
  // Medical history fields
  has_dm?: boolean;
  has_bp?: boolean;
  has_tsh?: boolean;
  has_acidity?: boolean;
  has_allergic?: boolean;
  allergic_details?: string;
  has_others?: boolean;
  others_details?: string;
  [key: string]: any;
}

function getCurrentMonthRange() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0],
  };
}

const PatientDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalVisits, setTotalVisits] = useState(0);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  
  // Add visit form state
  const [openAddVisitDialog, setOpenAddVisitDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [formData, setFormData] = useState({
    uid: '',
    datevisit: '',
    time: '',
    complaint: '',
    treatment: [] as string[],
    medicine: '',
    additional: '',
    follow: undefined as string | undefined,
    reference: '',
    fee: '',
    balance: '',
    credit: '0',
    payment_mode: '',
    attachment: null as File | null
  });

  // Edit patient form state
  const [openEditPatientDialog, setOpenEditPatientDialog] = useState(false);
  const [patientFormData, setPatientFormData] = useState({
    opd: '',
    name: '',
    age: '',
    birthdate: '',
    gender: '',
    address: '',
    mobile: '',
    datevisit: '',
    // Medical history fields - using boolean values
    has_dm: false,
    has_bp: false,
    has_tsh: false,
    has_acidity: false,
    has_allergic: false,
    allergic_details: '',
    has_others: false,
    others_details: ''
  });

  // Attachment modal state
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<string | null>(null);
  const [attachmentFileName, setAttachmentFileName] = useState<string>('');

  const { from: initialFrom, to: initialTo } = getCurrentMonthRange();
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setLoading(true);
        if (id === undefined) {
          setError('Invalid patient ID');
          setLoading(false);
          return;
        }
        const res = await api.getPatient(Number(id));
        const toBool = (v: any) => v === 1 || v === true;
        const patientData = res.data?.patient
          ? {
              ...res.data.patient,
              age: Number(res.data.patient.age),
              has_dm: toBool(res.data.patient.has_dm),
              has_bp: toBool(res.data.patient.has_bp),
              has_tsh: toBool(res.data.patient.has_tsh),
              has_acidity: toBool(res.data.patient.has_acidity),
              has_allergic: toBool(res.data.patient.has_allergic),
              has_others: toBool(res.data.patient.has_others),
            }
          : null;
        setPatient(patientData);
        setLoading(false);
      } catch (err: any) {
        setError('Failed to load patient details');
        setLoading(false);
      }
    };
    if (id) fetchPatient();
  }, [id]);

  useEffect(() => {
    const fetchMedicines = async () => {
      try {
        const res = await api.getMedicines({ active: 'active' });
        setMedicines(res.data?.medicines || []);
      } catch (err: any) {
        console.error('Failed to load medicines:', err);
      }
    };
    fetchMedicines();
  }, []);

  const fetchVisits = async () => {
    if (!id) return;
    try {
      setVisitsLoading(true);
      const res = await api.getPatientVisits(Number(id), page + 1, rowsPerPage);
      setVisits(res.data?.visits || []);
      setTotalVisits(res.data?.pagination?.totalItems || 0);
      setVisitsLoading(false);
    } catch (err: any) {
      console.error('Failed to load visits:', err);
      setVisitsLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits();
  }, [id, page, rowsPerPage]);


  // Handle scrolling to specific visit when visitId is provided in URL
  useEffect(() => {
    const visitId = searchParams.get('visitId');
    if (visitId && visits.length > 0) {
      // Find the visit in the current page
      const visitIndex = visits.findIndex(visit => visit.id === parseInt(visitId));
      if (visitIndex !== -1) {
        // Scroll to the visits table section
        const visitsTableElement = document.getElementById('visits-table');
        if (visitsTableElement) {
          visitsTableElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
          
          // Highlight the specific visit row
          setTimeout(() => {
            const visitRow = document.querySelector(`[data-visit-id="${visitId}"]`);
            if (visitRow) {
              visitRow.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
              // Add temporary highlight
              visitRow.classList.add('highlight-visit');
              setTimeout(() => {
                visitRow.classList.remove('highlight-visit');
              }, 3000);
            }
          }, 500);
        }
      }
    }
  }, [visits, searchParams]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    // Accepts "HH:mm" or "HH:mm:ss" or "HH:mm AM/PM"
    // If already in 12-hour format with AM/PM, return as is
    if (/(AM|PM)$/i.test(timeString.trim())) {
      return timeString;
    }
    // Otherwise, parse as 24-hour time
    const [hourStr, minute] = timeString.split(':');
    let hour = parseInt(hourStr, 10);
    let ampm = 'AM';
    if (isNaN(hour) || !minute) return timeString;
    if (hour === 0) {
      hour = 12;
      ampm = 'AM';
    } else if (hour === 12) {
      ampm = 'PM';
    } else if (hour > 12) {
      hour -= 12;
      ampm = 'PM';
    }
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  };

  // Get the most recent follow-up date that is today or in the future
  const getLastFollowUpDate = () => {
    if (!visits || visits.length === 0) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const futureFollowUps = visits
      .filter(visit => visit.follow)
      .map(visit => {
        let followDate: Date;
        if (typeof visit.follow === 'string') {
          followDate = new Date(visit.follow);
        } else if (visit.follow instanceof Date) {
          followDate = visit.follow;
        } else if (visit.follow) {
          followDate = new Date(visit.follow);
        } else {
          // This should not happen due to the filter, but handle it gracefully
          followDate = new Date();
        }
        return { visit, followDate };
      })
      .filter(({ followDate }) => !isNaN(followDate.getTime()) && followDate >= today)
      .sort((a, b) => a.followDate.getTime() - b.followDate.getTime());
    
    return futureFollowUps.length > 0 ? futureFollowUps[0] : null;
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount || '0');
    return `₹${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
  };

  // Calculate age from birthdate
  const calculateAge = (birthdate: string) => {
    if (!birthdate) return '';
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  // Handle birthdate change and auto-calculate age
  const handleBirthdateChange = (birthdate: string) => {
    setPatientFormData(prev => ({
      ...prev,
      birthdate,
      age: birthdate ? calculateAge(birthdate) : prev.age
    }));
  };

  // Add visit form handlers
  const handleAddVisit = () => {
    if (patient) {
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // Get HH:MM format
      
      setFormData({
        uid: patient.id.toString(),
        datevisit: new Date().toISOString().split('T')[0], // Today's date
        time: currentTime,
        complaint: '',
        treatment: [],
        medicine: '',
        additional: '',
        follow: undefined as string | undefined,
        reference: '',
        fee: '',
        balance: '',
        credit: '0',
        payment_mode: '',
        attachment: null
      });
      setEditingVisit(null);
      setOpenAddVisitDialog(true);
    }
  };

  const handleSubmitVisit = async () => {
    // Validate required fields
    if (!formData.complaint.trim()) {
      setSnackbar({ open: true, message: 'Complaint is required', severity: 'error' });
      return;
    }
    if (!formData.treatment || (Array.isArray(formData.treatment) && formData.treatment.length === 0)) {
      setSnackbar({ open: true, message: 'Treatment is required', severity: 'error' });
      return;
    }
    if (!formData.fee.trim()) {
      setSnackbar({ open: true, message: 'Fee is required', severity: 'error' });
      return;
    }
    if (!formData.balance.trim()) {
      setSnackbar({ open: true, message: 'Balance is required', severity: 'error' });
      return;
    }
    if (!formData.payment_mode.trim()) {
      setSnackbar({ open: true, message: 'Payment mode is required', severity: 'error' });
      return;
    }

    try {
      const visitData = {
        ...formData,
        treatment: Array.isArray(formData.treatment) ? formData.treatment.join(', ') : formData.treatment
      };

      // console.log('Form data before submission:', formData);
      // console.log('Visit data to send:', visitData);

      // Check if there's an attachment
      if (formData.attachment) {
        console.log('Attachment found:', formData.attachment);
        // Use FormData for file upload
        const formDataToSend = new FormData();
        
        // Append all fields except attachment
        Object.entries(visitData).forEach(([key, value]) => {
          if (key !== 'attachment') {
            formDataToSend.append(key, value as string);
            console.log(`Appending to FormData: ${key} = ${value}`);
          }
        });
        
        // Append the file
        formDataToSend.append('attachment', formData.attachment);
        console.log('Appending file to FormData:', formData.attachment.name);
        
        console.log('FormData created with attachment');
        
        if (editingVisit) {
          await api.updateVisit(editingVisit.id, formDataToSend);
          setSnackbar({ open: true, message: 'Visit updated successfully', severity: 'success' });
        } else {
          await api.createVisit(formDataToSend);
          setSnackbar({ open: true, message: 'Visit added successfully', severity: 'success' });
        }
      } else {
        console.log('No attachment, sending as JSON');
        // No attachment, send as JSON
        if (editingVisit) {
          console.log('Updating visit with data:', visitData);
          console.log('Visit ID:', editingVisit.id);
          await api.updateVisit(editingVisit.id, visitData);
          setSnackbar({ open: true, message: 'Visit updated successfully', severity: 'success' });
        } else {
          await api.createVisit(visitData);
          setSnackbar({ open: true, message: 'Visit added successfully', severity: 'success' });
        }
      }
      
      setOpenAddVisitDialog(false);
      setEditingVisit(null);
      // Refresh visits
      const res = await api.getPatientVisits(Number(id), page + 1, rowsPerPage);
      setVisits(res.data?.visits || []);
      setTotalVisits(res.data?.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Error saving visit:', error);
      setSnackbar({ open: true, message: editingVisit ? 'Failed to update visit' : 'Failed to add visit', severity: 'error' });
    }
  };

  const handleEditVisit = (visit: Visit) => {
    // Format datevisit as YYYY-MM-DD
    const formattedDate = visit.datevisit ? new Date(visit.datevisit).toISOString().split('T')[0] : '';
    // Format time as HH:MM for HTML time input (24-hour format)
    let formattedTime = visit.time || '';
    if (formattedTime) {
      // If time is like '14:30:00', trim to '14:30'
      if (formattedTime.length > 5) {
        formattedTime = formattedTime.slice(0, 5);
      }
      // Keep in 24-hour format for HTML time input
    }
    setEditingVisit(visit);
    setFormData({
      uid: visit.uid,
      datevisit: formattedDate,
      time: formattedTime,
      complaint: visit.complaint,
      treatment: visit.treatment ? visit.treatment.split(', ').filter(Boolean) : [],
      medicine: visit.medicine,
      additional: visit.additional,
      follow: visit.follow ? (typeof visit.follow === 'string' ? visit.follow : new Date(visit.follow).toISOString().split('T')[0]) : undefined,
      reference: visit.reference,
      fee: visit.fee,
      balance: visit.balance,
      credit: visit.credit,
      payment_mode: visit.payment_mode || '',
      attachment: null
    });
    setOpenAddVisitDialog(true);
  };

  const resetForm = () => {
    setFormData({
      uid: '',
      datevisit: '',
      time: '',
      complaint: '',
      treatment: [],
      medicine: '',
      additional: '',
      follow: undefined as string | undefined,
      reference: '',
      fee: '',
      balance: '',
      credit: '0',
      payment_mode: '',
      attachment: null
    });
    setEditingVisit(null);
  };

  // Delete visit function
  const handleDeleteVisit = async (visitId: number) => {
    if (window.confirm('Are you sure you want to delete this visit? This will hide it from the system but keep the data for record keeping.')) {
      try {
        await api.deleteVisit(visitId);
        setSnackbar({ open: true, message: 'Visit deleted successfully', severity: 'success' });
        // Refresh visits after deletion
        fetchVisits();
      } catch (error) {
        console.error('Error deleting visit:', error);
        setSnackbar({ open: true, message: 'Failed to delete visit', severity: 'error' });
      }
    }
  };

  // Edit patient functions
  const handleEditPatient = () => {
    if (patient) {
      // console.log('Loading patient data for edit:', patient);
      // console.log('Patient birthdate:', patient.birthdate);
      setPatientFormData({
        opd: patient.opd,
        name: patient.name,
        age: patient.age.toString(),
        birthdate: patient.birthdate ? (() => {
          // Handle timezone issues by parsing the date in local timezone
          const dateStr = patient.birthdate.split('T')[0]; // Get YYYY-MM-DD part
          const [year, month, day] = dateStr.split('-');
          // If the time component suggests it's the next day, adjust
          const timePart = patient.birthdate.split('T')[1];
          // console.log('Date string:', dateStr);
          // console.log('Time part:', timePart);
          // console.log('Includes 18:30?', timePart && timePart.includes('18:30'));
          
          if (timePart && timePart.includes('18:30')) {
            // This suggests the date was stored with a timezone offset
            // Add one day to get the correct date
            const correctedDay = parseInt(day) + 1;
            const correctedDate = `${year}-${month}-${String(correctedDay).padStart(2, '0')}`;
            // console.log('Corrected date:', correctedDate);
            return correctedDate;
          }
          // console.log('Using original date:', dateStr);
          return dateStr;
        })() : '',
        gender: patient.gender,
        address: patient.address || '',
        mobile: patient.mobile || '',
        datevisit: patient.datevisit || new Date().toISOString().split('T')[0],
        // Medical history fields - use the new boolean fields
        has_dm: patient.has_dm || false,
        has_bp: patient.has_bp || false,
        has_tsh: patient.has_tsh || false,
        has_acidity: patient.has_acidity || false,
        has_allergic: patient.has_allergic || false,
        allergic_details: patient.allergic_details || '',
        has_others: patient.has_others || false,
        others_details: patient.others_details || ''
      });
      console.log('Form data set:', {
        opd: patient.opd,
        name: patient.name,
        age: patient.age.toString(),
        birthdate: patient.birthdate ? (() => {
          // Handle timezone issues by parsing the date in local timezone
          const dateStr = patient.birthdate.split('T')[0]; // Get YYYY-MM-DD part
          const [year, month, day] = dateStr.split('-');
          // If the time component suggests it's the next day, adjust
          const timePart = patient.birthdate.split('T')[1];
          // console.log('Console - Date string:', dateStr);
          // console.log('Console - Time part:', timePart);
          // console.log('Console - Includes 18:30?', timePart && timePart.includes('18:30'));
          
          if (timePart && timePart.includes('18:30')) {
            // This suggests the date was stored with a timezone offset
            // Add one day to get the correct date
            const correctedDay = parseInt(day) + 1;
            const correctedDate = `${year}-${month}-${String(correctedDay).padStart(2, '0')}`;
            // console.log('Console - Corrected date:', correctedDate);
            return correctedDate;
          }
          // console.log('Console - Using original date:', dateStr);
          return dateStr;
        })() : '',
        gender: patient.gender,
        address: patient.address || '',
        mobile: patient.mobile || '',
        datevisit: patient.datevisit || new Date().toISOString().split('T')[0],
      });
      setOpenEditPatientDialog(true);
    }
  };

  const handleUpdatePatient = async () => {
    // Validate required fields
    if (!patientFormData.name.trim()) {
      setSnackbar({ open: true, message: 'Patient name is required', severity: 'error' });
      return;
    }
    if (!patientFormData.age.trim()) {
      setSnackbar({ open: true, message: 'Age is required', severity: 'error' });
      return;
    }
    if (!patientFormData.gender) {
      setSnackbar({ open: true, message: 'Gender is required', severity: 'error' });
      return;
    }
    if (!patientFormData.datevisit) {
      setSnackbar({ open: true, message: 'Date of first visit is required', severity: 'error' });
      return;
    }

    try {
      // console.log('Updating patient with data:', patientFormData);
      // console.log('Birthdate being sent:', patientFormData.birthdate);
      await api.updatePatient(patient!.id, patientFormData);
      setSnackbar({ open: true, message: 'Patient updated successfully', severity: 'success' });
      setOpenEditPatientDialog(false);
      
      // Refresh patient data
      const res = await api.getPatient(Number(id));
      const toBool = (v: any) => v === 1 || v === true;
      const patientData = res.data?.patient
        ? {
            ...res.data.patient,
            age: Number(res.data.patient.age),
            has_dm: toBool(res.data.patient.has_dm),
            has_bp: toBool(res.data.patient.has_bp),
            has_tsh: toBool(res.data.patient.has_tsh),
            has_acidity: toBool(res.data.patient.has_acidity),
            has_allergic: toBool(res.data.patient.has_allergic),
            has_others: toBool(res.data.patient.has_others),
          }
        : null;
      setPatient(patientData);
    } catch (error) {
      console.error('Error updating patient:', error);
      setSnackbar({ open: true, message: 'Failed to update patient', severity: 'error' });
    }
  };

  const handleCloseEditPatientDialog = () => {
    setOpenEditPatientDialog(false);
  };

  // Attachment modal handlers
  const handleOpenAttachment = (attachmentUrl: string, fileName: string) => {
    setSelectedAttachment(attachmentUrl);
    setAttachmentFileName(fileName);
    setOpenAttachmentModal(true);
  };

  const handleCloseAttachmentModal = () => {
    setOpenAttachmentModal(false);
    setSelectedAttachment(null);
    setAttachmentFileName('');
  };

  // Find earliest visit date for 'Date of 1st Visit'
  const firstVisitDate = visits.length > 0 ? visits[visits.length - 1].datevisit : undefined;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">Patient not found.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto', background: '#f7fafd', minHeight: '100vh' }}>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3, fontWeight: 600, borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}
        onClick={() => navigate('/patients')}
      >
        Back to All Patients
      </Button>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1976d2', letterSpacing: 1 }}>
        Patient Details
      </Typography>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3, border: '1.5px solid #e0e0e0', background: '#fff', position: 'relative', pl: 3, borderLeft: '6px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', letterSpacing: 1 }}>
              Patient Information
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditPatient}
              sx={{ 
                borderRadius: 2, 
                fontWeight: 600, 
                color: '#ff9800', 
                borderColor: '#ff9800',
                '&:hover': {
                  backgroundColor: '#ff9800',
                  color: '#fff',
                  borderColor: '#ff9800'
                }
              }}
            >
              Edit Patient
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, color: '#1976d2', letterSpacing: 1, mb: 1, lineHeight: 1.1 }}
            >
              {patient.name || 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>OPD</Typography>
              <Typography variant="body1" sx={{ fontWeight: 1000, fontSize: 18, color: '#d32f2f' }}>{patient.opd || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Age</Typography>
              <Typography variant="body1">{patient.age ? `${patient.age}` : 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Birthdate</Typography>
              <Typography variant="body1">
                {patient.birthdate ? formatDate(patient.birthdate) : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Gender</Typography>
              <Typography variant="body1">{patient.gender || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Address</Typography>
              <Typography variant="body1">{patient.address || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Mobile</Typography>
              <Typography variant="body1">{patient.mobile || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Date of 1st Visit</Typography>
              <Typography variant="body1">{firstVisitDate ? formatDate(firstVisitDate) : 'N/A'}</Typography>
            </Box>
            {(() => {
              // Get all follow-ups from visits
              const allFollowUps = visits
                .filter(visit => visit.follow)
                .map(visit => {
                  const followDate = typeof visit.follow === 'string' 
                    ? new Date(visit.follow) 
                    : visit.follow instanceof Date 
                      ? visit.follow 
                      : visit.follow 
                        ? new Date(visit.follow)
                        : new Date();
                  const isValidDate = !isNaN(followDate.getTime());
                  
                  return {
                    visit,
                    followDate: isValidDate ? followDate : null,
                    followText: isValidDate ? null : String(visit.follow),
                    isValidDate
                  };
                })
                .sort((a, b) => {
                  if (a.isValidDate && b.isValidDate) {
                    return b.followDate!.getTime() - a.followDate!.getTime();
                  }
                  return 0;
                });

              if (allFollowUps.length === 0) {
                return (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Follow Up</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No follow-up scheduled
                    </Typography>
                  </Box>
                );
              }

              const latestFollowUp = allFollowUps[0];
              
              return (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Follow Up</Typography>
                  {latestFollowUp.isValidDate ? (
                    (() => {
                      const followDate = latestFollowUp.followDate!;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      followDate.setHours(0, 0, 0, 0);
                      
                      // Check if patient has visited after this follow-up date
                      const hasVisitedAfterFollowUp = visits.some(otherVisit => {
                        if (otherVisit.id === latestFollowUp.visit.id) return false;
                        const visitDate = new Date(otherVisit.datevisit);
                        visitDate.setHours(0, 0, 0, 0);
                        return visitDate > followDate;
                      });
                      
                      const daysDiff = Math.ceil((followDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      
                      let chipColor: 'success' | 'warning' | 'error' | 'default';
                      if (hasVisitedAfterFollowUp) {
                        chipColor = 'default';
                      } else if (daysDiff >= 0) {
                        chipColor = 'success';
                      } else if (daysDiff >= -3) {
                        chipColor = 'warning';
                      } else {
                        chipColor = 'error';
                      }
                      
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)}
                            size="small"
                            color={chipColor}
                            sx={{ fontWeight: 600 }}
                          />
                          {!hasVisitedAfterFollowUp && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {daysDiff === 0 ? 'Today' : 
                               daysDiff === 1 ? 'Tomorrow' : 
                               daysDiff > 0 ? `In ${daysDiff} days` : 
                               `${Math.abs(daysDiff)} days ago`}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()
                  ) : (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      {latestFollowUp.followText || 'No follow-up text'}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
        </CardContent>
      </Card>

      {/* Medical History Card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2, border: '1.5px solid #e0e0e0', background: '#fff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
            Medical History
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="DM" variant={patient.has_dm ? "filled" : "outlined"} color={patient.has_dm ? "primary" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
            <Chip label="BP" variant={patient.has_bp ? "filled" : "outlined"} color={patient.has_bp ? "primary" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
            <Chip label="TSH" variant={patient.has_tsh ? "filled" : "outlined"} color={patient.has_tsh ? "primary" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
            <Chip label="Acidity" variant={patient.has_acidity ? "filled" : "outlined"} color={patient.has_acidity ? "primary" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
            <Chip label="Allergic" variant={patient.has_allergic ? "filled" : "outlined"} color={patient.has_allergic ? "warning" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
            <Chip label="Others" variant={patient.has_others ? "filled" : "outlined"} color={patient.has_others ? "secondary" : "default"} sx={{ fontWeight: 600, fontSize: 16 }} />
          </Box>
          {/* Allergies Details */}
          {patient.has_allergic && patient.allergic_details && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Allergy Details
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }} color="text.primary">
                {patient.allergic_details}
              </Typography>
            </Box>
          )}
          {/* Others Details */}
          {patient.has_others && patient.others_details && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Other Conditions/Medicines
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }} color="text.primary">
                {patient.others_details}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
        <Typography id="visit-history" variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1976d2' }}>Visit History</Typography>
        <Button
          variant="contained"
          sx={{ borderRadius: 2, fontWeight: 600, background: '#1976d2', minWidth: 150 }}
          onClick={handleAddVisit}
        >
          Add new visit
        </Button>
      </Box>

      {/* Financial Summary Card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2, border: '1.5px solid #e0e0e0', background: '#fff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
            Financial Summary
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Total Fees
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                {formatCurrency(visits.reduce((sum, visit) => sum + parseFloat(visit.fee || '0'), 0).toString())}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Total Balance
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: visits.reduce((sum, visit) => sum + parseFloat(visit.balance || '0'), 0) > 0 ? '#d32f2f' : '#2e7d32' }}>
                {formatCurrency(visits.reduce((sum, visit) => sum + parseFloat(visit.balance || '0'), 0).toString())}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Total Credit
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: visits.reduce((sum, visit) => sum + parseFloat(visit.credit || '0'), 0) > 0 ? '#1976d2' : '#2e7d32' }}>
                {formatCurrency(visits.reduce((sum, visit) => sum + parseFloat(visit.credit || '0'), 0).toString())}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Total Visits
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                {totalVisits}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: 2, border: '1.5px solid #e0e0e0', background: '#fff' }}>
        <CardContent>
          {visitsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : visits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No visits found for this patient.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer id="visits-table" component={Paper} sx={{ maxHeight: 600, borderRadius: 2, boxShadow: 1, border: '1.5px solid #bdbdbd', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 1400, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>No of Visits</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, position: 'sticky', left: 0, zIndex: 3 }}>Date of Visit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, position: 'sticky', left: 100, zIndex: 3 }}>Time of Visit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, maxWidth: 150 }}>Complaint</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, maxWidth: 120 }}>Treatment</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, maxWidth: 180 }}>Prescribed Medicine</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Fees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Credit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Follow Up</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2 }}>Attachment</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3eafc', border: '1px solid #bdbdbd', borderBottomWidth: 2, minWidth: 80 }}>Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visits.map((visit, idx) => (
                      <TableRow
                        key={visit.id}
                        data-visit-id={visit.id}
                        hover
                        onClick={() => handleEditVisit(visit)}
                        sx={{ 
                          backgroundColor: idx % 2 === 0 ? '#f7fafd' : '#fff',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#e3f2fd'
                          },
                          '&.highlight-visit': {
                            backgroundColor: '#fff3e0 !important',
                            border: '2px solid #ff9800 !important',
                            animation: 'pulse 2s ease-in-out'
                          }
                        }}
                      >
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd' }}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd', position: 'sticky', left: 0, zIndex: 2, backgroundColor: idx % 2 === 0 ? '#f7fafd' : '#fff', whiteSpace: 'nowrap' }}>{formatDate(visit.datevisit)}</TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd', position: 'sticky', left: 100, zIndex: 2, backgroundColor: idx % 2 === 0 ? '#f7fafd' : '#fff', whiteSpace: 'nowrap' }}>{formatTime(visit.time)}</TableCell>
                        <TableCell sx={{ border: '1px solid #bdbdbd' }} title={visit.complaint}>
                          {visit.complaint}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #bdbdbd' }} title={visit.treatment}>
                          {visit.treatment}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #bdbdbd' }} title={[visit.medicine, visit.additional].filter(Boolean).join(' / ') || 'No prescribed medicines'}>
                          {[visit.medicine, visit.additional].filter(Boolean).join(' / ') || '--'}
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd', fontWeight: 600, color: '#2e7d32' }}>
                          {formatCurrency(visit.fee)}
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd', fontWeight: 600, color: parseFloat(visit.balance || '0') > 0 ? '#d32f2f' : '#2e7d32' }}>
                          {formatCurrency(visit.balance)}
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd', fontWeight: 600, color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                          {formatCurrency(visit.credit)}
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd' }}>
                          {visit.follow ? (() => {
                            const followDate = typeof visit.follow === 'string' 
                              ? new Date(visit.follow) 
                              : new Date(visit.follow);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            followDate.setHours(0, 0, 0, 0);
                            
                            // Check if patient has visited after the follow-up date
                            const hasVisitedAfterFollowUp = visits.some(otherVisit => {
                              if (otherVisit.id === visit.id) return false; // Don't compare with same visit
                              const visitDate = new Date(otherVisit.datevisit);
                              visitDate.setHours(0, 0, 0, 0);
                              return visitDate > followDate;
                            });
                            
                            // If patient has visited after follow-up date, show like date of visit
                            if (hasVisitedAfterFollowUp) {
                              return (
                                <Typography variant="body2" align="center">
                                  {formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)}
                                </Typography>
                              );
                            }
                            
                            const daysDiff = Math.ceil((followDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            
                            let chipColor: 'success' | 'warning' | 'error' | 'default';
                            if (daysDiff >= 0) {
                              chipColor = 'success'; // Today or future - green
                            } else if (daysDiff >= -3) {
                              chipColor = 'warning'; // 1-3 days past - yellow
                            } else {
                              chipColor = 'error'; // More than 3 days past - red
                            }
                            
                            return (
                              <Chip 
                                label={formatDate(`${followDate.getFullYear()}-${String(followDate.getMonth() + 1).padStart(2, '0')}-${String(followDate.getDate()).padStart(2, '0')}`)} 
                                size="small" 
                                color={chipColor}
                                sx={{ fontWeight: 600 }}
                              />
                            );
                          })() : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No follow-up
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd' }}>
                          <Chip 
                            label={visit.payment_mode || 'N/A'} 
                            size="small" 
                            color={visit.payment_mode === 'Cash' ? 'success' : visit.payment_mode === 'Card' ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ border: '1px solid #bdbdbd' }}>
                          {visit.attachment ? (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Extract filename from attachment URL
                                const fileName = visit.attachment?.split('/').pop() || 'Attachment';
                                if (visit.attachment) {
                                  const fullUrl = api.getAttachmentUrl(visit.attachment);
                                  console.log('Opening attachment:', {
                                    originalPath: visit.attachment,
                                    fileName,
                                    fullUrl
                                  });
                                  handleOpenAttachment(fullUrl, fileName);
                                }
                              }}
                              title="View Attachment"
                            >
                              <AttachFile fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No attachment
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 80 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVisit(visit.id);
                            }}
                            color="error"
                            title="Delete Visit"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalVisits}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Visit Dialog */}
      <Dialog open={openAddVisitDialog} onClose={() => setOpenAddVisitDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingVisit ? `Edit Visit for ${patient?.name}` : `Add New Visit for ${patient?.name}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Date of Visit"
                  type="date"
                  value={formData.datevisit}
                  onChange={(e) => setFormData({ ...formData, datevisit: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Time of Visit"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>
            </Box>
            
            <Box>
              <TextField
                fullWidth
                label="Complaint"
                value={formData.complaint}
                onChange={(e) => setFormData({ ...formData, complaint: e.target.value })}
                multiline
                rows={2}
                required
              />
            </Box>
            
            <Box>
              <Autocomplete
                multiple
                options={medicines.map((medicine) => medicine.MedicineName)}
                value={formData.treatment}
                onChange={(_, newValue) => setFormData({ ...formData, treatment: newValue })}
                renderInput={(params) => (
                  <TextField {...params} label="Treatment" required />
                )}
                freeSolo
                autoComplete
                includeInputInList
                filterSelectedOptions
              />
            </Box>
            
            <Box>
              <TextField
                fullWidth
                label="Prescribed Medicine"
                value={formData.medicine}
                onChange={(e) => setFormData({ ...formData, medicine: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
            
            <Box>
              <TextField
                fullWidth
                label="Additional Notes"
                value={formData.additional}
                onChange={(e) => setFormData({ ...formData, additional: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Follow Up (Optional)"
                  type="date"
                  value={formData.follow || ''}
                  onChange={(e) => setFormData({ ...formData, follow: e.target.value || undefined })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Fee"
                  type="number"
                  value={formData.fee}
                  onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Balance"
                  type="number"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                  required
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Credit"
                  type="number"
                  value={formData.credit}
                  onChange={(e) => setFormData({ ...formData, credit: e.target.value })}
                />
              </Box>
            </Box>
            
            <Box>
              <FormControl fullWidth required>
                <InputLabel>Payment Mode</InputLabel>
                <Select
                  value={formData.payment_mode}
                  label="Payment Mode"
                  onChange={(e) => setFormData({ ...formData, payment_mode: e.target.value })}
                >
                  <MenuItem value="GPay">GPay</MenuItem>
                  <MenuItem value="Scanner">Scanner</MenuItem>
                  <MenuItem value="PhonePe">PhonePe</MenuItem>
                  <MenuItem value="Paytm">Paytm</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Free">Free</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <input
                accept="image/*,.pdf,.doc,.docx"
                style={{ display: 'none' }}
                id="attachment-file"
                type="file"
                onChange={(e) => setFormData({ ...formData, attachment: e.target.files?.[0] || null })}
              />
              <label htmlFor="attachment-file">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<AttachFile />}
                >
                  {formData.attachment ? formData.attachment.name : 'Attach File (Image, PDF, DOC)'}
                </Button>
              </label>
              {formData.attachment && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {formData.attachment.name} ({(formData.attachment.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              )}
              
              {/* Show current attachment preview if editing and no new file selected */}
              {editingVisit && editingVisit.attachment && !formData.attachment && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                    Current Attachment:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachFile sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {editingVisit.attachment.split('/').pop() || 'Attachment'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const fileName = editingVisit.attachment?.split('/').pop() || 'Attachment';
                        if (editingVisit.attachment) {
                          const fullUrl = api.getAttachmentUrl(editingVisit.attachment);
                          handleOpenAttachment(fullUrl, fileName);
                        }
                      }}
                    >
                      View
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select a new file above to replace this attachment
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddVisitDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitVisit} variant="contained">{editingVisit ? 'Update Visit' : 'Add Visit'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Add Visit */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Patient Dialog */}
      <Dialog open={openEditPatientDialog} onClose={handleCloseEditPatientDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Patient</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="OPD Number"
                value={patientFormData.opd}
                InputProps={{
                  readOnly: true,
                  style: { 
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    color: '#1976d2'
                  }
                }}
                required
              />
              <TextField
                fullWidth
                label="Patient Name"
                value={patientFormData.name}
                onChange={(e) => setPatientFormData({ ...patientFormData, name: e.target.value })}
                required
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Birthdate (Optional)"
                type="date"
                value={patientFormData.birthdate}
                onChange={(e) => handleBirthdateChange(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Age"
                type="number"
                value={patientFormData.age}
                onChange={(e) => setPatientFormData({ ...patientFormData, age: e.target.value })}
                required
                disabled={!!patientFormData.birthdate}
                helperText={patientFormData.birthdate ? "Age calculated from birthdate" : "Enter age manually"}
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={patientFormData.gender}
                  label="Gender"
                  onChange={(e) => setPatientFormData({ ...patientFormData, gender: e.target.value })}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={patientFormData.mobile}
                onChange={(e) => setPatientFormData({ ...patientFormData, mobile: e.target.value })}
                helperText="Optional"
              />
              <TextField
                fullWidth
                label="Date of First Visit"
                type="date"
                value={patientFormData.datevisit}
                onChange={(e) => setPatientFormData({ ...patientFormData, datevisit: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            
            <TextField
              fullWidth
              label="Address"
              value={patientFormData.address}
              onChange={(e) => setPatientFormData({ ...patientFormData, address: e.target.value })}
              multiline
              rows={3}
              helperText="Optional"
            />
            
            {/* Medical History Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                Medical History
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Medical Conditions Pills */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {[
                    { label: 'DM', field: 'has_dm' },
                    { label: 'BP', field: 'has_bp' },
                    { label: 'TSH', field: 'has_tsh' },
                    { label: 'Acidity', field: 'has_acidity' },
                    { label: 'Allergic', field: 'has_allergic' },
                    { label: 'Others', field: 'has_others' }
                  ].map((condition) => (
                    <Chip
                      key={condition.label}
                      label={condition.label}
                      variant={patientFormData[condition.field as keyof typeof patientFormData] ? "filled" : "outlined"}
                      color={patientFormData[condition.field as keyof typeof patientFormData] ? "primary" : "default"}
                      onClick={() => {
                        setPatientFormData({
                          ...patientFormData,
                          [condition.field]: !patientFormData[condition.field as keyof typeof patientFormData]
                        });
                      }}
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: 16,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: patientFormData[condition.field as keyof typeof patientFormData] ? '#1976d2' : '#f5f5f5'
                        }
                      }}
                    />
                  ))}
                </Box>
                
                {/* Medicine Details for Selected Conditions */}
                {(patientFormData.has_allergic || patientFormData.has_others) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Additional Details
                    </Typography>
                    
                    {patientFormData.has_allergic && (
                      <TextField
                        fullWidth
                        label="Allergy Details"
                        value={patientFormData.allergic_details}
                        onChange={(e) => setPatientFormData({ ...patientFormData, allergic_details: e.target.value })}
                        placeholder="Enter allergy details"
                      />
                    )}
                    
                    {patientFormData.has_others && (
                      <TextField
                        fullWidth
                        label="Other Conditions/Medicines"
                        value={patientFormData.others_details}
                        onChange={(e) => setPatientFormData({ ...patientFormData, others_details: e.target.value })}
                        placeholder="Enter other conditions or medicines"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditPatientDialog}>Cancel</Button>
          <Button onClick={handleUpdatePatient} variant="contained">Update Patient</Button>
        </DialogActions>
      </Dialog>

      {/* Attachment View Modal */}
      <Dialog 
        open={openAttachmentModal} 
        onClose={handleCloseAttachmentModal} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            minHeight: '60vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {attachmentFileName}
          </Typography>
          <IconButton onClick={handleCloseAttachmentModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {selectedAttachment && (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {selectedAttachment.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/) ? (
                // Image files
                <img 
                  src={selectedAttachment} 
                  alt={attachmentFileName}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh', 
                    objectFit: 'contain' 
                  }}
                  onLoad={() => console.log('Image loaded successfully:', selectedAttachment)}
                  onError={(e) => {
                    console.error('Image failed to load:', selectedAttachment, e);
                    console.error('Image error details:', {
                      src: selectedAttachment,
                      fileName: attachmentFileName,
                      error: e
                    });
                  }}
                  crossOrigin="anonymous"
                />
              ) : selectedAttachment.toLowerCase().match(/\.(pdf)$/) ? (
                // PDF files
                <iframe
                  src={selectedAttachment}
                  width="100%"
                  height="70vh"
                  title={attachmentFileName}
                  style={{ border: 'none' }}
                  onLoad={() => console.log('PDF loaded successfully:', selectedAttachment)}
                  onError={(e) => console.error('PDF failed to load:', selectedAttachment, e)}
                />
              ) : (
                // Other files - show download link
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <AttachFile sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {attachmentFileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This file type cannot be previewed. Click below to download.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => window.open(selectedAttachment, '_blank')}
                    startIcon={<AttachFile />}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* <Button variant="outlined" onClick={() => { setDateFrom(initialFrom); setDateTo(initialTo); }}>This Month</Button> */}
    </Box>
  );
};

export default PatientDetails;